import { useEffect, useMemo, useRef, useState } from "react";
import { getActiveModule, getBadgeParams, nameValidation, titleCase } from "@ts/utils";
import { useDispatch, useSelector } from "react-redux";
import { updateTrendsModal, updateTrendsTitle } from "@features/modalSlice";
import {
    trendClickHandler,
    selectDefaultAttributes,
} from "@ts/performance-overview";
import {
    updateAdGridFilters,
    updateCaGridFilters,
    updatePoGridFilters,
} from "@features/filtersSlice";
import {
    CAMPAIGN_SYNC_STATE,
    ColorHex,
    IActiveTab,
    ICheckedNode,
    ITargetCellRenderer,
    ServingStatus,
    SubModule,
} from "@ts/common-types";
import { RowNode } from "ag-grid-community";
import EditNameCellRenderer from "./EditNameCellRenderer";
import { Input, Tooltip } from "antd";
import { PiBroadcastFill } from "react-icons/pi";
import { TypeBadgeCellRenderer } from "./AllCellRenderers";
import { CAMPAIGN_SYNC_COLOR_MAP } from "@ts/constants";
import useGetModulesData from "@/hooks/useGetModulesData";
import useCampaignSyncCheck from "@/hooks/useCampaignSyncCheck";

const CampaignSyncStatus = ({
    status
}:{
    status: string;
})=>{

    const syncStatusColor = CAMPAIGN_SYNC_COLOR_MAP[status as keyof typeof CAMPAIGN_SYNC_COLOR_MAP];
    
    return (
        <Tooltip title={titleCase(status)} placement="topLeft">
            <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '18px',
                height: '18px',
                flexShrink: 0
            }}>
                <div style={{
                    width: '14px',
                    height: '14px',
                    borderRadius: '50%',
                    backgroundColor: syncStatusColor
                }}></div>
            </div>
        </Tooltip>
    )
}

const TargetCellRenderer: React.FC<ITargetCellRenderer> = (params) => {
    const inputRef = useRef(null);
    const servingStatus =
        params.data[
            ServingStatus[params.colDef.field as keyof typeof ServingStatus]
        ]?.toLowerCase();

    const campaignType = params.isTypeBadgeRequired
        ? params.data.targetingtype
        : "";

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.ctrlKey && (event.key === "a" || event.key === "A")) {
            event.preventDefault();
            inputRef.current.select();
        }
    };

    const activeTab = useSelector((state: IActiveTab) => state.activeTab.value);
    const dispatch = useDispatch();
    const { activeModule, activeParentModule } = getActiveModule();
    const gridParams = useGetModulesData({
        type: "grid-params",
        activeModuleName: activeModule,
        activeParentModuleName: activeParentModule,
    });
    const value = gridParams?.shouldRemoveTitleCase ? params.value || "" : titleCase(params.value || "");
    const trendsArg = useMemo(() => {
        let targetId = {};
        switch (activeTab) {
            case SubModule.KEYWORD:
                targetId = {
                    keyword_id: params.data.sys_ams_keyword_id,
                };
                break;
            case SubModule.PLACEMENTS:
                targetId = {
                    keyword_id: params.data.sys_ams_keyword_id,
                };
                break;

            case SubModule.CAMPAIGN:
                targetId = {
                    campaign_id: params.data.sys_campaign_id,
                };
                break;

            case SubModule.ADGROUP:
                targetId = {
                    adgroup_id: params.data.sys_adgroup_id,
                    campaign_id: params.data.sys_campaign_id,
                };
                break;
        }
        return targetId;
    }, [activeTab, params]);

    const identifier = useMemo(() => {
        let id = "";
        switch (activeTab) {
            case SubModule.CAMPAIGN:
                id = `${params.data.sys_campaign_id}-${params.data.program_type}`;
                break;

            case SubModule.ADGROUP:
                id = `${params.data.sys_adgroup_id}-${params.data.program_type}`;
                break;

            case SubModule.PORTFOLIO:
                id = `${params.data.portfolio_id}`;
                break;
        }
        return id;
    }, [activeTab, params]);

    const [inputValue, setInputValue] = useState<string>("");
    const [inputWidth, setInputWidth] = useState<number>(0);

    const caCheckedNode = useSelector(
        (state: ICheckedNode) => state.caCheckedNode.value
    );
    const adCheckedNode = useSelector(
        (state: ICheckedNode) => state.adCheckedNode.value
    );
    const poCheckedNode = useSelector(
        (state: ICheckedNode) => state.poCheckedNode.value
    );

    const checkedNodeHandler = (checkedNode: RowNode[]) => {
        if (checkedNode.length === 0) {
            switch (activeTab) {
                case "campaigns":
                    dispatch(updateCaGridFilters({}));
                    break;
                case "adgroups":
                    dispatch(updateAdGridFilters({}));
                    break;
                case "portfolios":
                    dispatch(updatePoGridFilters({}));
                    break;
            }
        }

    };

    const { showCampaignSyncStatus, isCampaignSyncCheckRequired, showNewTag } = useCampaignSyncCheck({params});

    useEffect(() => {
        //!Note: This is to disabled bulk actions for pending campaigns and prevent selection when "select all" is used
        if (showCampaignSyncStatus && params?.data?.sync_state !== CAMPAIGN_SYNC_STATE.SYNCED && params.node && !params.node.rowPinned) {
            const rowElement = document.querySelector(`[row-id="${params.node.id}"]`);
            if (rowElement) {
                rowElement.classList.add('ag-row-checkbox-disabled');
            }
            if (params.node.isSelected()) {
                params.node.setSelected(false);
            }

            const selectionListener = () => {
                if (params.node.isSelected()) {
                    params.node.setSelected(false);
                }
            };
            
            params.node.addEventListener('rowSelected', selectionListener);
            
            return () => {
                params.node.removeEventListener('rowSelected', selectionListener);
            };
        }
    }, [showCampaignSyncStatus, params.node, params.data?.sync_state]);

    useEffect(() => {
        switch (activeTab) {
            case "campaigns":
                checkedNodeHandler(caCheckedNode);
                setInputValue(params.data.campaign_name);
                break;
            case "adgroups":
                checkedNodeHandler(adCheckedNode);
                setInputValue(params.data.ad_group_name);
                break;
            case "portfolios":
                checkedNodeHandler(poCheckedNode);
                setInputValue(params.data.portfolio_name);
                break;
        }
        // eslint-disable-next-line
    }, [activeTab, caCheckedNode, adCheckedNode]);

    return (
        <>
            {params.node.rowPinned ? (
                params.pinnedColumnValue
            ) : (
                <>
                    <span
                        id={`${identifier}-edit-container`}
                        className="d-flex justify-content-between"
                        style={{ gap: "8px" }}
                    >
                        {activeTab === "portfolios" ? (
                            <span id={identifier}>{value}</span>
                        ) : (
                                        <div className="d-flex flex-column" style={{ flex: 1, minWidth: 0 }}>
                                            <div
                                            className="anchor-class"
                                            style={{
                                                lineHeight: 1.8,
                                                fontSize: 13.5,
                                                marginTop: activeTab !== SubModule.KEYWORD ? 5 : 0,
                                                minWidth: 0,
                                            }}
                                            id={identifier}
                                            onClick={async () => {
                                                await trendClickHandler(
                                                    trendsArg,
                                                    activeTab
                                                );
                                                selectDefaultAttributes();
                                                dispatch(updateTrendsModal(true));
                                                dispatch(updateTrendsTitle(value));
                                            }}
                                        >
                                            <div
                                                id={`${identifier}-name`}
                                                style={{
                                                    width: "100%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "4px",
                                                    minWidth: 0,
                                                }}
                                            >
                                                {/* {isCampaignSyncStatusRequired && <CampaignSyncStatus status={"synced"} />} */}
                                                {showCampaignSyncStatus && <CampaignSyncStatus status={params.data.sync_state} />}
                                                <span style={{
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    flex: 1,
                                                    minWidth: 0,
                                                }}>
                                                    {value}
                                                </span>
                                                {
                                                    params.data.is_smart &&
                                                    <PiBroadcastFill style={{
                                                        color: ColorHex.GREY,
                                                        fontSize: "18px",
                                                        marginLeft: "7px",
                                                        flexShrink: 0,
                                                    }} />
                                                }
                                            </div>
                                            <div>

                                            </div>
                                            {params.isBadgeRequired && (
                                                <div className="d-flex align-items-center gap-2" style={{
                                                    marginTop: activeTab !== SubModule.KEYWORD ? 5 : 3,
                                                }}>
                                                    {params.isTypeBadgeRequired && (
                                                        <TypeBadgeCellRenderer {...{ params }} />
                                                    )}
                                                    <div
                                                        className="badge text-secondary"
                                                        style={{
                                                            width: "fit-content",
                                                            border: `1px solid ${getBadgeParams(
                                                                "color",
                                                                "status",
                                                                servingStatus,
                                                                campaignType
                                                            )}`,
                                                        }}
                                                >
                                                        {getBadgeParams(
                                                            "text",
                                                            "status",
                                                            servingStatus,
                                                            campaignType
                                                        )}
                                                    </div>
                                                    {
                                                        showNewTag && <div
                                                        className="d-flex align-items-center justify-content-center gap-1 badge text-white p-1"
                                                        style={{
                                                            width: "fit-content",
                                                            backgroundColor: getBadgeParams(
                                                                "color",
                                                                "new"
                                                            ),
                                                        }}
                                                >
                                                    <span className="bg-white rounded-circle" style={{
                                                        width: 5,
                                                        height: 5,
                                                    }}></span>
                                                        {getBadgeParams(
                                                            "text",
                                                            "new"
                                                        )}
                                                    </div>
                                                    }
                                                </div>
                                            )}
                                        </div>
                                        </div>
                        )}
                            {activeTab !== "keywords" && (
                            <span
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-around",
                                    }}
                                >
                                    <Input
                                        ref={inputRef}
                                        id={`${identifier}-update-field`}
                                        name="name-change-input"
                                        className="border border-primary"
                                        value={inputValue}
                                        onChange={(e) =>
                                            setInputValue(
                                                nameValidation(e.target.value)
                                            )
                                        }
                                        onKeyDown={handleKeyDown}
                                        style={{
                                            display: "none",
                                            fontSize: "0.875rem",
                                            width: inputWidth,
                                        }}
                                    />
                                    <EditNameCellRenderer
                                    {...{
                                        params,
                                        setInputWidth,
                                        setInputValue,
                                        isCampaignSyncCheckRequired
                                    }}
                                />
                                </span>
                            )}
                    </span>
                </>
            )}
        </>
    );
};

export default TargetCellRenderer;
